/** Top-level Campaign tab entities (distinct from nested profile section cards). */

export const CAMPAIGN_ENTITY_VARIANTS = {
  recruitingProfile: {
    id: "recruitingProfile",
    badge: "Recruiting profile",
    accent: "brand500",
    border: "brand200",
    surface: "brand50",
    headerFrom: "brand50",
  },
  leadTargeting: {
    id: "leadTargeting",
    badge: "Lead targeting",
    accent: "success600",
    border: "success700",
    surface: "success50",
    headerFrom: "success50",
  },
}

function color(theme, key, fallback) {
  return theme.palette.colors?.[key] ?? fallback
}

/**
 * @param {import("@mui/material").Theme} theme
 * @param {"recruitingProfile" | "leadTargeting"} variant
 */
export function campaignEntityPanelSx(theme, variant) {
  const v = CAMPAIGN_ENTITY_VARIANTS[variant]
  const accent = color(theme, v.accent, theme.palette.primary.main)
  const border = color(theme, v.border, theme.palette.divider)

  return {
    width: "100%",
    borderRadius: 2,
    overflow: "hidden",
    bgcolor: "background.paper",
    border: `1px solid ${border}`,
    borderLeftWidth: 5,
    borderLeftColor: accent,
    boxShadow:
      variant === "leadTargeting"
        ? "0 2px 12px -4px rgba(23, 135, 84, 0.14), 0 1px 4px -2px rgba(52, 64, 84, 0.06)"
        : "0 2px 12px -4px rgba(137, 47, 206, 0.14), 0 1px 4px -2px rgba(52, 64, 84, 0.06)",
  }
}

/**
 * @param {import("@mui/material").Theme} theme
 * @param {"recruitingProfile" | "leadTargeting"} variant
 */
export function campaignEntityHeaderSx(theme, variant) {
  const v = CAMPAIGN_ENTITY_VARIANTS[variant]
  const accent = color(theme, v.accent, theme.palette.primary.main)
  const surface = color(theme, v.surface, theme.palette.action.hover)
  const headerTint = color(theme, v.headerFrom, surface)

  return {
    px: { xs: 2, sm: 2.5 },
    py: 2,
    borderBottom: `1px solid ${color(theme, v.border, theme.palette.divider)}`,
    background: `linear-gradient(135deg, ${headerTint}88 0%, rgba(255, 255, 255, 0.97) 70%)`,
    "& .campaign-entity-icon": {
      color: accent,
      bgcolor: "background.paper",
      border: `1px solid ${color(theme, v.border, theme.palette.divider)}`,
    },
  }
}

/**
 * @param {import("@mui/material").Theme} theme
 * @param {"recruitingProfile" | "leadTargeting"} variant
 */
export function campaignEntityBadgeSx(theme, variant) {
  const v = CAMPAIGN_ENTITY_VARIANTS[variant]
  const accent = color(theme, v.accent, theme.palette.primary.main)
  const surface = color(theme, v.surface, theme.palette.action.hover)

  return {
    height: 24,
    fontWeight: 700,
    fontSize: "0.7rem",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    bgcolor: surface,
    color: accent,
    border: `1px solid ${accent}44`,
    "& .MuiChip-label": { px: 1 },
  }
}

/**
 * @param {import("@mui/material").Theme} theme
 * @param {"recruitingProfile" | "leadTargeting"} variant
 */
export function campaignEntityEditButtonSx(theme, variant) {
  const v = CAMPAIGN_ENTITY_VARIANTS[variant]
  const accent = color(theme, v.accent, theme.palette.primary.main)

  return {
    textTransform: "none",
    fontWeight: 600,
    flexShrink: 0,
    borderColor: `${accent}66`,
    color: accent,
    "&:hover": {
      borderColor: accent,
      bgcolor: color(theme, v.surface, theme.palette.action.hover),
    },
  }
}

export function campaignEntityTimestampChipSx(theme, variant) {
  const v = CAMPAIGN_ENTITY_VARIANTS[variant]
  const accent = color(theme, v.accent, theme.palette.primary.main)

  return {
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 26,
    borderColor: `${accent}55`,
    color: accent,
    maxWidth: "100%",
    "& .MuiChip-label": { px: 1.25 },
  }
}
